import type { EventType } from '@/tracking/snowplow/utils';
import { SNOWPLOW_EVENTS_MAPPING } from '@/tracking/snowplow/utils';
import { getBannerClickedProps } from '@/tracking/snowplow/events/banner-clicked';
import { getBannerScrolledProps } from '@/tracking/snowplow/events/banner-scrolled';
import { getInitialLoadProps } from '@/tracking/snowplow/events/initial-load';
import { getZoneInteractionProps } from '@/tracking/snowplow/events/zone-interaction';
import { getScreenViewProps } from '@/tracking/snowplow/events/screen-view';
import { getInfoIconClickedProps } from '@/tracking/snowplow/events/info-icon-clicked';

import {
    getSearchInitializedProps,
    getSearchOpenedProps,
    getSearchResultsReturnedProps,
    getSearchResultsSelectedProps
} from '@/tracking/snowplow/events/search/search';

const EVENT_PROP_MAPPINGS = {
    game_clicked: getBannerClickedProps(),
    top_nav_impression: getInitialLoadProps(),
    top_nav_scroll: getBannerScrolledProps(),
    top_nav_selected: getBannerClickedProps(),
    zone_initial_load: getInitialLoadProps(),
    zone_scrolled: getBannerScrolledProps(),
    game_zone_impression: getZoneInteractionProps(),
    game_zone_scroll: getZoneInteractionProps(),
    game_zone_selected: getZoneInteractionProps(),
    category_page_impression: getZoneInteractionProps(),
    category_page_game_selected: getZoneInteractionProps(),
    zone_interaction: getZoneInteractionProps(),
    category_screen_view: getScreenViewProps(),
    game_favorited: {},
    game_favorite_removed: {},
    info_icon_clicked: getInfoIconClickedProps(),
    search_opened: getSearchOpenedProps(),
    search_initialized: getSearchInitializedProps(),
    search_results_returned: getSearchResultsReturnedProps(),
    search_results_selected: getSearchResultsSelectedProps()
};

type PropMapping = string | { key: string; fn: (...args: unknown[]) => unknown; params?: string[] };

export const mapPropsToKeys = (props: Record<string, unknown>, eventName: string): Record<string, unknown> => {
    if (!props || !Object.keys(props).length) {
        return {};
    }

    const { CONTEXTS } = SNOWPLOW_EVENTS_MAPPING;

    const mapping = (EVENT_PROP_MAPPINGS[eventName as EventType] ?? CONTEXTS[eventName]) as
        | Record<string, PropMapping>
        | undefined;

    if (!mapping) {
        return {};
    }

    const result: Record<string, unknown> = {};

    for (const [propKey, propValue] of Object.entries(props)) {
        const mapConfig = mapping[propKey];

        if (!mapConfig) {
            continue;
        }

        if (typeof mapConfig === 'string') {
            result[mapConfig] = propValue;
        } else {
            const params = mapConfig.params ?? [];
            const paramValues = params.map((param) => props[param] ?? null);
            const computedValue = mapConfig.fn(...paramValues);

            if (computedValue !== null && computedValue !== undefined) {
                result[mapConfig.key] = computedValue;
            }
        }
    }

    return result;
};
